import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { productionApi as P, PRIORITY_STYLE, STAGE_TITLE } from "@/lib/production";
import { StatusChip } from "@/components/production/StatusChip";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ScanLine, Play, Pause, RotateCcw, CheckCircle2, Loader2, Thermometer, AlertTriangle } from "lucide-react";

export default function OperatorWorkspace() {
  const { stage } = useParams();
  const { user } = useAuth();
  const [meta, setMeta] = useState(null);
  const [machines, setMachines] = useState([]);
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null); // opened task
  const [scan, setScan] = useState("");
  // start controls
  const [machineId, setMachineId] = useState("");
  const [inputBatch, setInputBatch] = useState("");
  const [fabricBatch, setFabricBatch] = useState("");
  const [checklistOk, setChecklistOk] = useState(false);
  const [tempReady, setTempReady] = useState(false);
  const [busy, setBusy] = useState(false);
  // finish
  const [finishOpen, setFinishOpen] = useState(false);
  const [fin, setFin] = useState({ meters_out: "", wastage: "0", wastage_reason: "", shrinkage: "0", grade: "A", packages: "1", width: "", ink_consumption: "", temperature: "", speed: "", pressure: "" });

  const cfg = meta?.stages?.[stage]?.fields || {};
  const sm = meta?.stages?.[stage] || {};
  const reasons = meta?.stages?.[stage] || { pause_reasons: [], wastage_reasons: [] };

  const refresh = useCallback(async () => {
    try {
      const [q] = await Promise.all([P.queue(stage)]);
      setQueue(q);
      setActive((prev) => (prev ? q.find((t) => t.id === prev.id) || prev : prev));
    } catch (e) { console.error("Failed to load operator queue", e); }
  }, [stage]);

  useEffect(() => {
    setLoading(true);
    setActive(null);
    (async () => {
      try {
        const [m, mac, q] = await Promise.all([P.meta(), P.machines(stage), P.queue(stage)]);
        setMeta(m); setMachines(mac); setQueue(q);
      } finally { setLoading(false); }
    })();
  }, [stage]);

  useEffect(() => {
    const id = setInterval(refresh, 5000);
    return () => clearInterval(id);
  }, [refresh]);

  const openTask = (t, preselectBatch) => {
    setActive(t);
    setMachineId(t.assigned_machine_id || "");
    setInputBatch(preselectBatch || (t.available_inputs[0]?.id || ""));
    setFabricBatch(t.available_fabric_inputs?.[0]?.id || "");
    setChecklistOk(false); setTempReady(false);
  };

  const doScan = () => {
    const code = scan.trim();
    if (!code) return;
    const t = queue.find((x) => x.available_inputs.some((b) => b.id === code));
    if (t) { openTask(t, code); toast.success(`Roll ${code} loaded`); }
    else toast.error("Roll not recognised or not ready for this stage.");
    setScan("");
  };

  const remaining = active ? Math.max(0, active.target_meters - active.done_meters) : 0;

  const doStart = async () => {
    setBusy(true);
    try {
      await P.start(active.id, { machine_id: machineId, batch_id: inputBatch, fabric_batch_id: cfg.secondary_input ? fabricBatch : undefined, checklist_ok: checklistOk, machine_ready: tempReady });
      toast.success("Started");
      await refresh();
      const t = await P.task(active.id); setActive(t);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start"); }
    finally { setBusy(false); }
  };

  const doPause = async (reason) => {
    try { await P.pause(active.id, reason); toast("Paused"); const t = await P.task(active.id); setActive(t); await refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const doResume = async () => {
    try { await P.resume(active.id); toast.success("Resumed"); const t = await P.task(active.id); setActive(t); await refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const openFinish = () => {
    setFin({ meters_out: String(remaining || ""), wastage: "0", wastage_reason: "", shrinkage: "0", grade: "A", packages: "1", width: "", ink_consumption: "", temperature: "", speed: "", pressure: "" });
    setFinishOpen(true);
  };
  const doFinish = async () => {
    setBusy(true);
    try {
      await P.finish(active.id, {
        meters_out: Number(fin.meters_out), wastage: Number(fin.wastage), wastage_reason: fin.wastage_reason,
        shrinkage: cfg.shrinkage ? Number(fin.shrinkage) : 0, grade: cfg.grade ? fin.grade : null,
        packages: cfg.packages ? Number(fin.packages) : 1,
        width: fin.width ? Number(fin.width) : null,
        ink_consumption: fin.ink_consumption ? Number(fin.ink_consumption) : null,
        temperature: fin.temperature ? Number(fin.temperature) : null,
        speed: fin.speed ? Number(fin.speed) : null,
        pressure: fin.pressure ? Number(fin.pressure) : null,
      });
      toast.success("Roll finished");
      setFinishOpen(false); setActive(null);
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not finish"); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-indigo-600" /></div>;

  const mySession = active?.my_active_session;

  return (
    <div className="space-y-5" data-testid={`operator-${stage}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-900">{STAGE_TITLE[stage]} — Operator</h2>
          <p className="text-sm text-slate-500">{user?.name} · scan a roll or pick a job to begin</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <ScanLine className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input value={scan} onChange={(e) => setScan(e.target.value)} onKeyDown={(e) => e.key === "Enter" && doScan()}
              placeholder="Scan / enter roll ID" className="w-56 pl-9" data-testid={`${stage}-scan-input`} />
          </div>
          <Button variant="outline" onClick={doScan} data-testid={`${stage}-scan-btn`}>Load</Button>
        </div>
      </div>

      <div className="grid gap-5 lg:grid-cols-[340px_1fr]">
        {/* Queue */}
        <div className="space-y-3">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Queue ({queue.length})</p>
          {queue.length === 0 && (
            <Card className="border-dashed p-6 text-center text-sm text-slate-400" data-testid="operator-empty-queue">
              No {stage} jobs right now. New rolls appear here automatically as the previous stage completes.
            </Card>
          )}
          {queue.map((t) => (
            <Card key={t.id} onClick={() => openTask(t)} data-testid={`job-card-${t.job_no}`}
              className={`cursor-pointer p-4 transition-all hover:border-indigo-300 ${active?.id === t.id ? "ring-2 ring-indigo-500" : ""}`}>
              <div className="flex items-center justify-between">
                <span className="font-heading font-semibold text-slate-900">{t.job_no}</span>
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${PRIORITY_STYLE[t.priority]}`}>{t.priority}</span>
              </div>
              <p className="mt-0.5 text-xs text-slate-500">{t.customer}</p>
              <div className="mt-2"><Progress value={(t.done_meters / t.target_meters) * 100} className="h-1.5" /></div>
              <div className="mt-1 flex items-center justify-between text-xs text-slate-500">
                <span>{t.done_meters} / {t.target_meters} m</span>
                <StatusChip status={t.status} />
              </div>
            </Card>
          ))}
        </div>

        {/* Active panel */}
        <div>
          {!active ? (
            <Card className="flex h-full min-h-[300px] flex-col items-center justify-center gap-2 border-dashed p-8 text-center text-slate-400" data-testid="operator-no-active">
              <ScanLine className="h-8 w-8" />
              <p>Scan a roll or tap a job to start {stage}.</p>
            </Card>
          ) : (
            <Card className="p-6" data-testid="active-job-panel">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                  {stage === "printing" && active.artwork?.thumbnail_url && (
                    <img src={active.artwork.thumbnail_url} alt="artwork" className="h-16 w-16 rounded-md object-cover" />
                  )}
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-heading text-xl font-bold text-slate-900">{active.job_no}</h3>
                      <StatusChip status={active.status} testid="active-status" />
                    </div>
                    <p className="text-sm text-slate-500">{active.customer} · target {active.target_meters} m · due {active.delivery_date || "—"}</p>
                    {stage === "printing" && (
                      <p className="mt-1 text-xs text-slate-500">Artwork <span className="font-medium">{active.artwork?.version}</span>{active.artwork?.locked ? " · locked ✓" : " · NOT LOCKED"}</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-4">
                <Progress value={(active.done_meters / active.target_meters) * 100} className="h-2" />
                <p className="mt-1 text-sm tabular-nums text-slate-600">{active.done_meters} / {active.target_meters} m · {remaining} m remaining</p>
              </div>

              {/* Printing gate warning */}
              {stage === "printing" && !active.artwork?.locked && (
                <div className="mt-4 flex items-center gap-2 rounded-md bg-rose-50 p-3 text-sm text-rose-700">
                  <AlertTriangle className="h-4 w-4" /> Artwork not locked — printing is blocked.
                </div>
              )}

              {!mySession ? (
                <div className="mt-5 space-y-4 border-t border-slate-100 pt-5">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label>{sm.input_label || "Input roll"}</Label>
                      <Select value={inputBatch} onValueChange={setInputBatch}>
                        <SelectTrigger data-testid="input-roll-select"><SelectValue placeholder="Select roll" /></SelectTrigger>
                        <SelectContent>
                          {active.available_inputs.map((b) => <SelectItem key={b.id} value={b.id}>{b.id} · {b.meters_out} m</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Machine</Label>
                      <Select value={machineId} onValueChange={setMachineId}>
                        <SelectTrigger data-testid="machine-select"><SelectValue placeholder="Select machine" /></SelectTrigger>
                        <SelectContent>
                          {machines.map((m) => <SelectItem key={m.machine_id} value={m.machine_id}>{m.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  {cfg.secondary_input && (
                    <div className="space-y-1.5">
                      <Label>{sm.secondary_input_label || "Grey fabric roll"}</Label>
                      <Select value={fabricBatch} onValueChange={setFabricBatch}>
                        <SelectTrigger data-testid="fabric-roll-select"><SelectValue placeholder="Select grey fabric roll" /></SelectTrigger>
                        <SelectContent>
                          {(active.available_fabric_inputs || []).map((b) => <SelectItem key={b.id} value={b.id}>{b.id} · {b.meters_out} m</SelectItem>)}
                        </SelectContent>
                      </Select>
                      {(active.available_fabric_inputs || []).length === 0 && <p className="text-xs text-amber-600">No grey fabric roll issued for this job — Planning must reserve one.</p>}
                    </div>
                  )}
                  {cfg.checklist && (
                    <label className="flex items-center gap-2 text-sm text-slate-700">
                      <Checkbox checked={checklistOk} onCheckedChange={setChecklistOk} data-testid="checklist-toggle" />
                      Pre-print checklist done (paper loaded · ink OK · nozzle check)
                    </label>
                  )}
                  {cfg.temperature_gate && (
                    <label className="flex items-center gap-2 text-sm text-slate-700">
                      <Checkbox checked={tempReady} onCheckedChange={setTempReady} data-testid="temp-ready-toggle" />
                      <Thermometer className="h-4 w-4 text-rose-500" /> Machine has reached target temperature
                    </label>
                  )}
                  <Button onClick={doStart} disabled={busy || !inputBatch || !machineId || (cfg.secondary_input && !fabricBatch)} data-testid={`${stage}-start-btn`}
                    className="w-full bg-indigo-600 py-6 text-base text-white hover:bg-indigo-700">
                    {busy ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <Play className="mr-2 h-5 w-5" />}Start {STAGE_TITLE[stage]}
                  </Button>
                </div>
              ) : (
                <div className="mt-5 space-y-3 border-t border-slate-100 pt-5">
                  <p className="text-sm text-slate-500">Running on <span className="font-medium">{mySession.machine_id}</span> · roll {mySession.batch_id}</p>
                  <div className="grid grid-cols-2 gap-3">
                    {mySession.status === "active" ? (
                      <Select onValueChange={doPause}>
                        <SelectTrigger className="border-amber-300 text-amber-700" data-testid={`${stage}-pause-btn`}>
                          <span className="flex items-center gap-2"><Pause className="h-4 w-4" />Pause…</span>
                        </SelectTrigger>
                        <SelectContent>
                          {reasons.pause_reasons.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Button variant="outline" onClick={doResume} data-testid={`${stage}-resume-btn`} className="border-indigo-300 text-indigo-700">
                        <RotateCcw className="mr-2 h-4 w-4" />Resume
                      </Button>
                    )}
                    <Button onClick={openFinish} data-testid={`${stage}-finish-btn`} className="bg-emerald-600 text-white hover:bg-emerald-700">
                      <CheckCircle2 className="mr-2 h-4 w-4" />Finish
                    </Button>
                  </div>
                </div>
              )}
            </Card>
          )}
        </div>
      </div>

      {/* Finish dialog */}
      <Dialog open={finishOpen} onOpenChange={setFinishOpen}>
        <DialogContent data-testid="finish-dialog">
          <DialogHeader>
            <DialogTitle>Finish — {active?.job_no}</DialogTitle>
            <DialogDescription>Record the {sm.output_label || "output"} for {stage === "fusing" ? "this paper + fabric run" : `roll ${mySession?.batch_id}`}.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>{{ printing: "Printed paper meters", fusing: "Finished fabric meters", cutting: "Cut quantity", stitching: "Stitched products", packing: "Bundled products" }[stage] || "Output quantity"}</Label>
              <Input type="number" value={fin.meters_out} onChange={(e) => setFin({ ...fin, meters_out: e.target.value })} data-testid="finish-meters-input" />
            </div>
            {stage === "printing" && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Paper width (in)</Label><Input type="number" value={fin.width} onChange={(e) => setFin({ ...fin, width: e.target.value })} data-testid="finish-width-input" /></div>
                <div className="space-y-1.5"><Label>Ink used (ml, optional)</Label><Input type="number" value={fin.ink_consumption} onChange={(e) => setFin({ ...fin, ink_consumption: e.target.value })} data-testid="finish-ink-input" /></div>
              </div>
            )}
            {stage === "fusing" && (
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1.5"><Label>Temp (°C)</Label><Input type="number" value={fin.temperature} onChange={(e) => setFin({ ...fin, temperature: e.target.value })} data-testid="finish-temp-input" /></div>
                <div className="space-y-1.5"><Label>Speed</Label><Input type="number" value={fin.speed} onChange={(e) => setFin({ ...fin, speed: e.target.value })} data-testid="finish-speed-input" /></div>
                <div className="space-y-1.5"><Label>Pressure</Label><Input type="number" value={fin.pressure} onChange={(e) => setFin({ ...fin, pressure: e.target.value })} data-testid="finish-pressure-input" /></div>
              </div>
            )}
            {cfg.shrinkage && (
              <div className="space-y-1.5">
                <Label>Shrinkage (m)</Label>
                <Input type="number" value={fin.shrinkage} onChange={(e) => setFin({ ...fin, shrinkage: e.target.value })} data-testid="finish-shrinkage-input" />
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Wastage (m)</Label>
                <Input type="number" value={fin.wastage} onChange={(e) => setFin({ ...fin, wastage: e.target.value })} data-testid="finish-wastage-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Wastage reason</Label>
                <Select value={fin.wastage_reason} onValueChange={(v) => setFin({ ...fin, wastage_reason: v })}>
                  <SelectTrigger data-testid="finish-wastage-reason"><SelectValue placeholder="Reason" /></SelectTrigger>
                  <SelectContent>{reasons.wastage_reasons.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            {cfg.grade && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Grade</Label>
                  <Select value={fin.grade} onValueChange={(v) => setFin({ ...fin, grade: v })}>
                    <SelectTrigger data-testid="finish-grade-select"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="A">A-grade</SelectItem><SelectItem value="B">B-grade</SelectItem></SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Packages</Label>
                  <Input type="number" min="1" value={fin.packages} onChange={(e) => setFin({ ...fin, packages: e.target.value })} data-testid="finish-packages-input" />
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFinishOpen(false)}>Cancel</Button>
            <Button onClick={doFinish} disabled={busy} data-testid="confirm-finish-btn" className="bg-emerald-600 text-white hover:bg-emerald-700">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
